import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface AdminPaginationProps {
  page: number
  onPrev: () => void
  onNext: () => void
  /** 提供時會顯示頁碼按鈕，可直接跳頁 */
  onJumpTo?: (page: number) => void
  totalPages?: number
  totalItems?: number
  pageSize?: number
  hasMore?: boolean
  count?: number
  loading?: boolean
}

/**
 * 用 first/last/current/neighbors 模式產生頁碼陣列：
 *   1 ... 4 5 [6] 7 8 ... 20
 * 0 = ellipsis
 */
function buildPageNumbers(current: number, total: number): number[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages = new Set<number>([1, total, current])
  for (const offset of [-1, 1, -2, 2]) {
    const p = current + offset
    if (p >= 1 && p <= total) pages.add(p)
  }
  const sorted = [...pages].sort((a, b) => a - b)
  const out: number[] = []
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push(0) // ellipsis
    out.push(sorted[i])
  }
  return out
}

export function AdminPagination({
  page,
  onPrev,
  onNext,
  onJumpTo,
  totalPages,
  totalItems,
  pageSize,
  hasMore,
  count,
  loading,
}: AdminPaginationProps) {
  const canPrev = page > 1 && !loading
  const canNext = (totalPages != null ? page < totalPages : !!hasMore) && !loading
  const hasTotals = totalPages != null
  const showNumbers = hasTotals && onJumpTo != null && totalPages! > 1

  let infoText: string
  if (totalItems != null && pageSize != null) {
    const from = (page - 1) * pageSize + 1
    const to = Math.min(page * pageSize, totalItems)
    infoText = `第 ${from}–${to} 筆，共 ${totalItems} 筆`
  } else if (count != null) {
    infoText = `顯示 ${count} 筆`
  } else {
    infoText = ""
  }

  let pageText: string
  if (hasTotals) {
    pageText = `第 ${page} / ${totalPages} 頁`
  } else if (hasMore) {
    pageText = `第 ${page} 頁 · 還有更多`
  } else if (page === 1) {
    pageText = `僅一頁`
  } else {
    pageText = `第 ${page} 頁 · 最後一頁`
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
      <div className="text-xs text-muted-foreground">
        {infoText}
        {infoText && " · "}
        {pageText}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={!canPrev}>
          上一頁
        </Button>
        {showNumbers &&
          buildPageNumbers(page, totalPages!).map((p, i) =>
            p === 0 ? (
              <span key={`e-${i}`} className="px-2 text-xs text-muted-foreground">
                …
              </span>
            ) : (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                size="sm"
                className={cn("min-w-[2.25rem]", p === page && "pointer-events-none")}
                onClick={() => onJumpTo!(p)}
                disabled={loading}
              >
                {p}
              </Button>
            )
          )}
        <Button variant="outline" size="sm" onClick={onNext} disabled={!canNext}>
          下一頁
        </Button>
      </div>
    </div>
  )
}
